import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { getLocalStorageData } from "../services/localStorageService";
import { postApiWithAuth } from "../services/api/api";

function Header() {
  const navigate = useNavigate();
  const [color, setColor] = useState(false);
  const location = useLocation();
  const [scrollPosition, setScrollPosition] = useState(0);

  // Dynamic Marquee Text State
  const [marqueeText, setMarqueeText] = useState("");

  // 1. Recover core authorization tokens and string records from localStorage
  const token = getLocalStorageData("token");
  const isLoggedIn = !!token && token !== "undefined" && token !== "null";
  const userDetailsString = getLocalStorageData("userDetails");
  const profileDataString = getLocalStorageData("profile_data");

  // Define default fallback UI elements
  let userProfileName = "User";
  let userProfileImage = `${process.env.PUBLIC_URL}/assets/imgs/default_user.jpeg`;

  // 2. Compute dynamic name and image fields using priority rules
  if (isLoggedIn) {
    let profile = null;
    let userDetails = null;

    // Parse the profile_data array wrapper cleanly
    if (profileDataString) {
      try {
        const parsedProfile = JSON.parse(profileDataString);
        profile = Array.isArray(parsedProfile) ? parsedProfile[0] : parsedProfile;
      } catch (e) {
        console.error("Error parsing profile_data in Header:", e);
      }
    }

    // Parse secondary user details object layout
    if (userDetailsString) {
      try {
        userDetails = JSON.parse(userDetailsString);
      } catch (e) {
        console.error("Error parsing userDetails in Header:", e);
      }
    }

    // PRIORITY LOGIC FOR USER DISPLAY NAME: profile_data -> userDetails -> Fallback
    if (profile && (profile.first_name || profile.last_name)) {
      userProfileName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
    } else if (userDetails) {
      userProfileName =
          userDetails.name ||
          `${userDetails.first_name || ""} ${userDetails.last_name || ""}`.trim();
    }

    // PRIORITY LOGIC FOR PROFILE PICTURE: profile_data main_photo URL -> Fallback Icon
    if (profile && profile.main_photo && profile.main_photo.trim() !== "") {
      userProfileImage = profile.main_photo;
    }
  }

  // 3. Fetch Dynamic Marquee Text from API
  useEffect(() => {
    const fetchMarqueeText = async () => {
      try {
        const response = await postApiWithAuth("/marquee", {});
        // Adjust extraction logic based on your exact API response structure
        const fetchedText = response?.data?.text;

        if (fetchedText) {
          setMarqueeText(fetchedText);
        }
      } catch (error) {
        console.error("Error fetching marquee text:", error);
      }
    };

    fetchMarqueeText();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
      setColor(window.scrollY >= 90);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // One-click logout handler to flush caches and safely redirect
  const handleLogoutClick = (e) => {
    e.preventDefault();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
    window.location.reload(); // Force app re-mount to refresh navigation states cleanly
  };

  const closeMenu = () => {
    const navbar = document.getElementById("navbarSupportedContent");
    navbar?.classList.remove("show");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const menu = document.getElementById("navbarSupportedContent");
      const toggler = document.querySelector(".navbar-toggler");

      if (
          menu &&
          menu.classList.contains("show") &&
          !menu.contains(e.target) &&
          !toggler?.contains(e.target)
      ) {
        menu.classList.remove("show");
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const formatName = (name) => {
    if (!name || typeof name !== "string") return "NA";
    return name
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  };

  return (
      <div className="sticky-top">
        {/* Conditionally render marquee only if text is available */}
        {marqueeText && (
            <div className="marquee">
              <div className="marquee-track">
                {Array.from({ length: 8 }).map((_, i) => (
                    <span key={i}>{marqueeText}</span>
                ))}
              </div>
            </div>
        )}

        <header
            className={
              location.pathname === "/"
                  ? color
                      ? "scroll-header-bg"
                      : "landing-header"
                  : "inner-header"
            }
        >
          <nav className="navbar navbar-expand-lg bg-body-tertiary main-navbar">
            <div className="container">
              {/* Logo - Environment Safe Pathing */}
              <Link
                  className="navbar-brand d-flex align-items-center"
                  to={isLoggedIn ? "/dashboard" : "/"}
              >
                <img
                    className="logo"
                    src={`${process.env.PUBLIC_URL}/assets/imgs/vmm_logo.png`}
                    alt="Logo"
                />
                <p className="logo_name">Vikas Mandal Mulund</p>
              </Link>

              {/* Mobile Toggle */}
              <button
                  className="navbar-toggler"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarSupportedContent"
                  aria-controls="navbarSupportedContent"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div className="collapse navbar-collapse" id="navbarSupportedContent">
                <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
                  {!isLoggedIn ? (
                      <li className="nav-item">
                        <Link className="nav-link active" to="/" onClick={closeMenu}>
                          Home
                        </Link>
                      </li>
                  ) : (
                      <li className="nav-item">
                        <Link
                            className="nav-link active"
                            to="/dashboard"
                            onClick={closeMenu}
                        >
                          Dashboard
                        </Link>
                      </li>
                  )}

                  <li className="nav-item">
                    <Link className="nav-link" to="/AboutUs" onClick={closeMenu}>
                      About Us
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link
                        className="nav-link"
                        to="/ManagementTeam"
                        onClick={closeMenu}
                    >
                      Management
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link className="nav-link" to="/Events" onClick={closeMenu}>
                      Events
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link className="nav-link" to="/ContactUs" onClick={closeMenu}>
                      Contact us
                    </Link>
                  </li>
                </ul>

                {/* Dynamic Dropdown for Logged-In User Profile */}
                {isLoggedIn ? (
                    <div className="d-flex">
                      <div className="dropdown-center">
                        <div
                            className="loign_user dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            style={{ cursor: "pointer" }}
                        >
                          <img
                              className="login_user_profile"
                              src={userProfileImage}
                              alt="User Profile"
                              style={{ objectFit: "cover", borderRadius: "50%" }}
                              onError={(e) => {
                                e.target.src = `${process.env.PUBLIC_URL}/assets/imgs/default_user.jpeg`;
                              }}
                          />
                          <h5 className="login_user_profile_name">
                            {formatName(userProfileName)}
                          </h5>
                        </div>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                          <li>
                            <Link
                                className="dropdown-item py-2"
                                to="/dashboard"
                                onClick={closeMenu}
                            >
                              <i className="fa-regular fa-user me-2 text-secondary"></i>{" "}
                              My Dashboard
                            </Link>
                          </li>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <a
                                className="dropdown-item py-2 text-danger fw-bold"
                                href="#!"
                                onClick={handleLogoutClick}
                            >
                              <i className="fa-solid fa-arrow-right-from-bracket me-2"></i>{" "}
                              Logout
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                ) : null}
              </div>
            </div>
          </nav>
        </header>
      </div>
  );
}

export default Header;